import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import BaseCard from '../../../../components/base/BaseCard';
import MatrixHeader from './MatrixHeader';
import MatrixStats from './MatrixStats';
import MatrixTable from './MatrixTable';
import MatrixFooter from './MatrixFooter';
import { useMatrixCalculations } from '../../hooks/useMatrixCalculations';

const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-4">
        <div className="h-10 bg-slate-800/50 rounded-lg w-3/4" />
        <div className="grid grid-cols-4 gap-8">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-2">
                    <div className="h-4 bg-slate-800/50 rounded w-1/2" />
                    <div className="h-8 bg-slate-800/50 rounded" />
                </div>
            ))}
        </div>
    </div>
);

const ExtrapolationMatrix = ({
    positionType,
    nominalRR,
    effectiveRR,
    risk,
    accountBalance,
    disabled = false,
    isLoading = false
}) => {
    const [currentWinRate, setCurrentWinRate] = useState(55);
    const [maxDailyLosses, setMaxDailyLosses] = useState(2);
    const [totalTrades, setTotalTrades] = useState(100);
    // The whole extrapolation section (inputs + table) is collapsed by default,
    // available in both Full and Compact. The heavy table only mounts when open.
    const [open, setOpen] = useState(false);

    const {
        breakEvenRate,
        winRates,
        currentStats,
        calculateStats
    } = useMatrixCalculations(
        effectiveRR,
        risk,
        accountBalance,
        totalTrades,
        currentWinRate,
        maxDailyLosses
    );

    return (
        <div className="space-y-4">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="w-full bg-gradient-to-b from-slate-900 to-slate-900/95 border border-white/10 rounded-xl px-4 sm:px-8 py-4 flex items-center justify-between text-left hover:border-white/20 transition-colors"
            >
                <div className="min-w-0 pr-3">
                    <span className="text-slate-300/90 text-sm sm:text-base font-medium">Extrapolation</span>
                    <span className="text-slate-500 text-xs sm:text-sm block sm:inline sm:ml-2">projected results over many trades</span>
                </div>
                <ChevronDown
                    size={18}
                    className={`text-slate-500 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <>
                    <BaseCard>
                        <div className="p-4 sm:p-8">
                            <div className="space-y-6">
                                {isLoading ? (
                                    <LoadingSkeleton />
                                ) : (
                                    <>
                                        <MatrixHeader
                                            totalTrades={totalTrades}
                                            setTotalTrades={setTotalTrades}
                                            currentWinRate={currentWinRate}
                                            setCurrentWinRate={setCurrentWinRate}
                                            maxDailyLosses={maxDailyLosses}
                                            setMaxDailyLosses={setMaxDailyLosses}
                                            disabled={disabled}
                                        />

                                        <div className="py-2">
                                            <MatrixStats
                                                stats={currentStats}
                                                isLoading={isLoading}
                                            />

                                            <div className="mt-8">
                                                <MatrixFooter
                                                    maxLossDays={currentStats.maxLossDays}
                                                    currentWinRate={currentWinRate}
                                                    maxDailyLosses={maxDailyLosses}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </BaseCard>

                    <div className="bg-slate-900 p-4 sm:p-8 rounded-xl border border-slate-800">
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-slate-300/90 text-lg font-medium">Extrapolation</h3>
                                <div className="flex gap-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-slate-600"></div>
                                        <span className="text-slate-400 text-sm">Break-even</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-primary-orange"></div>
                                        <span className="text-slate-400 text-sm">Current</span>
                                    </div>
                                </div>
                            </div>

                            <MatrixTable
                                winRates={winRates}
                                breakEvenRate={breakEvenRate}
                                currentWinRate={currentWinRate}
                                totalTrades={totalTrades}
                                getStatsForRate={calculateStats}
                                isLoading={isLoading}
                            />

                            <div className="space-y-1.5">
                                <div className="text-xs text-slate-500">
                                    * Max Loss Streak calculated using d'Alembert's formula with 99% confidence level
                                </div>
                                <div className="text-xs text-slate-500">
                                    * All calculations include entry fee and exit fee
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ExtrapolationMatrix;
